"use client";

import { motion, animate, useInView } from "framer-motion";
import { BookingDrawer } from "./BookingDrawer";
import { useState, useRef, useEffect } from "react";

const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
};

function PriceCounter({ value }: { value: number }) {
  const [displayValue, setDisplayValue] = useState("0");
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      const controls = animate(0, value, {
        duration: 2,
        ease: "easeOut",
        onUpdate(v) {
          setDisplayValue(v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
        },
      });
      return () => controls.stop();
    }
  }, [value, isInView]);

  return <span ref={ref}>{displayValue}</span>;
}

export function Product() {
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  return (
    <section id="product" className="relative px-6 md:px-12 py-32 border-t border-border overflow-hidden">
      <div className="max-w-[1600px] mx-auto text-center">
        <motion.div
          className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground mb-8"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          [Featured Offer]
        </motion.div>

        <motion.h2
          className="font-display uppercase leading-[0.82] tracking-[-0.03em] mb-8"
          style={{ fontSize: "clamp(3rem, 10vw, 12rem)" }}
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          The Single <span className="text-outline-acid italic font-serif normal-case tracking-tight">Bundle.</span>
        </motion.h2>

        <motion.p
          className="font-serif text-2xl md:text-4xl text-muted-foreground max-w-2xl mx-auto mb-16 leading-tight"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          One price. One hit. Full production included.
        </motion.p>

        <motion.div
          className="mb-20 flex flex-col items-center"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-4">
            All-inclusive fee
          </span>
          <div className="font-display text-[oklch(0.94_0.27_128)] drop-shadow-[0_0_40px_oklch(0.94_0.27_128/0.4)] leading-none"
            style={{ fontSize: "clamp(5rem, 15vw, 12rem)" }}>
            $ <PriceCounter value={1350} />
          </div>
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-6 opacity-60">
            Limited Time Launch Price
          </span>
        </motion.div>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <button 
            onClick={() => setIsBookingOpen(true)}
            className="btn-acid px-16 py-10 text-2xl md:text-3xl acid-glow inline-block cursor-pointer"
          >
            <span>→ SECURE YOUR SESSION</span>
          </button>
        </motion.div>

        <BookingDrawer open={isBookingOpen} onOpenChange={setIsBookingOpen} />
      </div>
    </section>
  );
}

